import React, { useState, createContext, useContext } from "react";
import { useOracleSyncTrigger } from "../../hooks/useOracleSyncTrigger";
import styles from "./SyncTriggerForm.module.css";

const SyncTriggerContext = createContext();

/**
 * Compound component for triggering Oracle ERP sync
 * Usage:
 * <SyncTriggerForm onSuccess={handleSuccess}>
 *     <SyncTriggerForm.Header />
 *     <SyncTriggerForm.InputArea />
 *     <SyncTriggerForm.SubmitButton />
 *     <SyncTriggerForm.StatusSection />
 * </SyncTriggerForm>
 */
const SyncTriggerForm = ({ children, onSuccess }) => {
    const [textInput, setTextInput] = useState("");
    const { triggerSync, loading, error, result, reset } = useOracleSyncTrigger();

    const handleSubmit = async () => {
        // Parse movement IDs from text input
        const movementIds = textInput
            .split(/[\n,\s]+/)
            .map(id => id.trim())
            .filter(id => id.length > 0);

        const response = await triggerSync(movementIds);

        if (response && onSuccess) {
            setTextInput("");
            onSuccess(response);
        }
    };

    const value = {
        textInput,
        setTextInput,
        handleSubmit,
        loading,
        error,
        result,
        reset
    };

    return (
        <SyncTriggerContext.Provider value={value}>
            <div className={styles.container}>
                {children}
            </div>
        </SyncTriggerContext.Provider>
    );
};


const Header = () => {
    return <h3 className={styles.headerTitle}>Iniciar Sincronización Oracle</h3>;
};

const InputArea = () => {
    const { textInput, setTextInput, reset } = useContext(SyncTriggerContext);

    const handleChange = (e) => {
        reset();
        setTextInput(e.target.value);
    };

    return (
        <div className={styles.inputContainer}>
            <label className={styles.label}>IDs de Movimientos</label>
            <textarea
                className={styles.textArea}
                placeholder="Ingresa los IDs de movimientos separados por coma, espacio o salto de línea..."
                value={textInput}
                onChange={handleChange}
            />
        </div>
    );
};

const SubmitButton = () => {
    const { handleSubmit, loading, textInput } = useContext(SyncTriggerContext);

    const isEmpty = !textInput.trim();

    return (
        <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={loading || isEmpty}
        >
            {loading && <span className={styles.spinner}></span>}
            {loading ? "Enviando..." : "Enviar"}
        </button>
    );
};

const StatusSection = () => {
    const { error, result } = useContext(SyncTriggerContext);

    if (!error && !result) return null;

    if (error) {
        return (
            <div className={`${styles.statusContainer} ${styles.statusError}`}>
                ❌ {error}
            </div>
        );
    }

    if (result) {
        return (
            <div className={`${styles.statusContainer} ${styles.statusSuccess}`}>
                ✅ Solicitud enviada correctamente. Request ID: <span className={styles.requestId}>{result.request_id}</span>
            </div>
        );
    }

    return null;
};

// Attach subcomponents
SyncTriggerForm.Header = Header;
SyncTriggerForm.InputArea = InputArea;
SyncTriggerForm.SubmitButton = SubmitButton;
SyncTriggerForm.StatusSection = StatusSection;

export default SyncTriggerForm;
